import { AuditDetailItem } from '@frontend/audit/components';
import { EntityType } from '@frontend/common';
import { ErrorHandler } from '@frontend/error-handler';
import { ICONS } from '@frontend/icons';
import { CommonMessage } from '@frontend/lang';
import { useModals } from '@frontend/modal';
import { MqttBroker } from '@frontend/pub-sub';
import { buildCollectionMqttTopic, getResolveFunction, resolveEntityMqttAccountId, useAccountRepository, useReactiveObject } from '@frontend/repository';
import { ToastUtil } from '@frontend/toast-utils';
import React, { JSX, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { DetailItem, DetailItemProps } from './detail-item/detail-item.component';
import { buildObjectUpdatePath, resolveObjectDetailBaseRoute } from './object-detail-navigation';
import { ObjectDetailProps } from './object-detail.component';

interface ViewProps<T> {
    object: T | null;
    onDeleteConfirmed: () => void;
    onUpdate: () => void;
    subSections: DetailItemProps[];
    filteredSubSections: DetailItemProps[];
    activeSubSection: string | undefined;
    changeActiveSubSection: React.Dispatch<React.SetStateAction<string | undefined>>;
    main: React.ReactElement[];
    onChange: (identifier: string) => void;
    openModal: ReturnType<typeof useModals>['openModal'];
    closeModal: ReturnType<typeof useModals>['closeModal'];
    deleteModalId: string;
    navigate: ReturnType<typeof useNavigate>;
}

const noopResolve = () => '';

const useObjectDetail = <T,>(props: ObjectDetailProps<T>): ViewProps<T> => {
    const navigate = useNavigate();
    const params = useParams();
    const { currentAccount } = useAccountRepository();
    const [object, changeObject] = useState<T | null>(null);
    const urlParams = useMemo(() => props.urlParamKeys.map((key) => params[key]), [props.urlParamKeys, params]);
    const urlParamsKey = urlParams.join('/');
    const prevUrlParamsKeyRef = useRef(urlParamsKey);
    const [id] = useState<string>(crypto.randomUUID());
    const [subSections, changeSubSections] = useState<DetailItemProps[]>([
        { identifier: 'Main', label: CommonMessage.DETAIL.MAIN, icon: ICONS.DETAIL.MAIN, children: [] }
    ]);
    const [main, changeMain] = useState<React.ReactElement[]>([]);
    const [activeSubSection, changeActiveSubSection] = useState<string | undefined>(subSections ? subSections[0].identifier : undefined);
    const [searchParams, setSearchParams] = useSearchParams();
    const { openModal, closeModal } = useModals();
    const deleteModalId = 'object-detail-delete-modal';

    const reactiveEntityType = props.entityType ?? props.eventListener;
    const reactiveEntityId = props.reactive && reactiveEntityType ? (urlParams[urlParams.length - 1] as string | undefined) : undefined;

    const {
        object: reactiveObject,
        initialising: reactiveInitialising,
        isLoading: reactiveLoading
    } = useReactiveObject<{ id: string }>({
        id: reactiveEntityId,
        entity_type: reactiveEntityType ?? EntityType.ACCOUNT,
        resolveFunction: (entityId, options) => {
            if (!reactiveEntityType) {
                return noopResolve();
            }
            const registered = getResolveFunction(reactiveEntityType);
            return registered ? registered(entityId, options) : noopResolve();
        },
        fetchOnload: true,
        skipResolveTimeout: true
    });

    const finalSubSections = useMemo(() => {
        const auditItem = AuditDetailItem({ ...props, openModal, closeModal }, object);
        return [...subSections, auditItem];
    }, [subSections, props, object, openModal, closeModal]);

    useEffect(() => {
        if (searchParams && searchParams.get('menu') && finalSubSections.filter((section) => section.identifier !== 'Main').length > 0) {
            changeActiveSubSection(searchParams.get('menu') as string);
        } else {
            changeActiveSubSection(finalSubSections[0].identifier.toLowerCase());
        }
    }, [finalSubSections, searchParams]);

    const onChange = (identifier: string) => {
        setSearchParams((prev) => {
            const search = new URLSearchParams(prev);
            search.set('menu', identifier.toLowerCase());
            return search.toString();
        });
    };

    useEffect(() => {
        if (object && props.children) {
            React.Children.forEach(props.children(object), (child) => {
                if (!React.isValidElement(child)) return;
                if (child.type === DetailItem) {
                    processDetailItem(child);
                } else {
                    const children = (child.props as any).children;
                    if (children) {
                        React.Children.forEach(children, (subChild) => {
                            if (!React.isValidElement(subChild)) return;
                            if (subChild.type === DetailItem) {
                                processDetailItem(subChild);
                            }
                        });
                    }
                }
            });
        }
    }, [props.children, object]);

    const processDetailItem = (detailItem: JSX.Element) => {
        if (detailItem.props.main) {
            changeMain((prev) => {
                const existingIndex = prev.findIndex((item) => item.key === detailItem.key);
                if (existingIndex !== -1) {
                    const updatedMain = [...prev];
                    updatedMain[existingIndex] = detailItem;
                    return updatedMain;
                } else {
                    return [...prev, detailItem];
                }
            });
        } else {
            changeSubSections((prev) => {
                const existingIndex = prev.findIndex((item) => item.identifier === detailItem.props.identifier);
                if (existingIndex !== -1) {
                    const updatedSubSections = [...prev];
                    updatedSubSections[existingIndex] = detailItem.props;
                    return updatedSubSections;
                } else {
                    return [...prev, detailItem.props];
                }
            });
        }
    };

    useEffect(() => {
        if (object) {
            props.object && props.object(object);
        }
    }, [object]);

    const onFetch = () => {
        if (urlParams.includes(undefined)) {
            ToastUtil.error('Something went wrong trying to get the correct information.');
            navigate(resolveObjectDetailBaseRoute(props.baseRoute));
        } else
            props
                .fetch(...urlParams)
                .then(changeObject)
                .catch((e) => ErrorHandler.handleError({ error: e }));
    };

    const objectAccountId = (object as unknown as { account_id?: string } | null)?.account_id;
    const mqttAccountId = resolveEntityMqttAccountId(objectAccountId, currentAccount);

    // Legacy path: collection MQTT topic (unchanged when reactive is off).
    useEffect(() => {
        if (props.reactive) {
            return;
        }
        if (!mqttAccountId) return;
        onFetch();
        if (props.eventListener == undefined || !props.dispatch) return;
        const onEvent = (message: string) => {
            if (message.includes('created') || message.includes('deleted') || message.includes('multi') || message.includes('update')) onFetch();
        };
        const topic = buildCollectionMqttTopic(mqttAccountId, props.eventListener);
        MqttBroker.getInstance().subscribe(id, topic, onEvent);
        return () => {
            MqttBroker.getInstance().unSubscribe(id, topic);
        };
    }, [mqttAccountId, props.reactive]);

    // Reactive path: refetch when URL id or the per-object reactive signal changes; clear on delete.
    useEffect(() => {
        if (!props.reactive) {
            return;
        }
        if (!currentAccount) {
            return;
        }
        if (prevUrlParamsKeyRef.current !== urlParamsKey) {
            prevUrlParamsKeyRef.current = urlParamsKey;
            changeObject(null);
        }
        if (reactiveEntityId && !reactiveInitialising && !reactiveLoading && reactiveObject === null) {
            changeObject(null);
            return;
        }
        onFetch();
    }, [props.reactive, currentAccount, urlParamsKey, reactiveObject, reactiveInitialising, reactiveLoading, reactiveEntityId]);

    const onDeleteConfirmed = () => {
        props.delete
            ?.onDelete(...urlParams)
            .then(() => {
                closeModal(deleteModalId);
                if (props.delete?.onDeleteSuccess) props.delete.onDeleteSuccess();
                else navigate('../../');
            })
            .catch((e) => {
                ErrorHandler.handleError({ error: e });
            });
    };

    const onUpdate = () => {
        if (props.update?.onUpdate) props.update.onUpdate();
        else {
            navigate(
                buildObjectUpdatePath({
                    urlParamKeys: props.urlParamKeys,
                    updateKeys: props.updateKeys,
                    navigationKeyReplacements: props.navigationKeyReplacements,
                    params,
                    isAdminPath: location.pathname.startsWith('/admin')
                })
            );
        }
    };

    return {
        object,
        onDeleteConfirmed,
        onUpdate,
        subSections: finalSubSections,
        changeActiveSubSection,
        activeSubSection,
        filteredSubSections: finalSubSections.filter((section) => section.identifier !== 'Main'),
        main,
        onChange,
        closeModal,
        openModal,
        deleteModalId,
        navigate
    };
};

export default useObjectDetail;
